import React from 'react';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Snackbar } from '@mui/material';
import EditableGridToolbar from './EditableGridToolbar';
import EditableGridTable from './EditableGridTable';
import { FEditableGridProps, FEditableGridRow } from './types';
import { compileRows, createEmptyRow, makeRid, validateRows } from './utils';
import EditableGridBody from './EditableGridBody';

const FEditableGrid: React.FC<FEditableGridProps> = ({
  gform, value, onChange, readonly = false, title, height, allowAdd = true, allowDelete = true,
  allowDuplicate = true, allowPreview = true, onPreview, onSubmit,
}) => {
  const [rows, setRows] = React.useState<FEditableGridRow[]>(value || [createEmptyRow(gform)]);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState('');

  React.useEffect(() => { if (value) setRows(value); }, [value]);

  const emit = (next: FEditableGridRow[]) => { setRows(next); onChange?.(next); };
  const addRow = () => emit([...rows, createEmptyRow(gform)]);
  const toggleRow = (rid: string, selected: boolean) => emit(rows.map((r) => r._rid === rid ? { ...r, _selected: selected } : r));
  const changeCell = (rid: string, colid: string, cellValue: any) => emit(rows.map((r) => r._rid === rid ? { ...r, [colid]: cellValue, _state: r._state === 'new' ? 'new' : 'dirty' } : r));
  const deleteSelected = () => emit(rows.map((r) => r._selected ? { ...r, _state: 'deleted', _selected: false } : r));
  const duplicateSelected = () => {
    const copies = rows.filter((r) => r._selected && r._state !== 'deleted').map((r) => ({ ...r, _rid: makeRid(), _state: 'new' as const, _selected: false }));
    emit([...rows.map((r) => ({ ...r, _selected: false })), ...copies]);
  };

  const preview = () => {
    const checked = validateRows(gform, rows);
    emit(checked.rows);
    if (!checked.ok) { setMessage('Please correct required fields before preview.'); return; }
    onPreview?.(compileRows(checked.rows));
    setPreviewOpen(true);
  };

  const submit = async () => {
    const checked = validateRows(gform, rows);
    emit(checked.rows);
    if (!checked.ok) { setMessage('Please correct required fields before submitting.'); return; }
    setSubmitting(true);
    try { await onSubmit?.(compileRows(checked.rows)); setMessage('Submitted successfully.'); }
    catch (err: any) { setMessage(err.message || String(err)); }
    finally { setSubmitting(false); }
  };

  const hasSelection = rows.filter((r) => r._state !== 'deleted').some((r) => r._selected);

  return (
    <Box>
      <EditableGridToolbar title={title} readonly={readonly} allowAdd={allowAdd} allowDelete={allowDelete} allowDuplicate={allowDuplicate} allowPreview={allowPreview} hasSelection={hasSelection} submitting={submitting} onAdd={addRow} onDuplicate={duplicateSelected} onDelete={deleteSelected} onPreview={preview} onSubmit={submit} />
      {/* <EditableGridTable gform={gform} rows={rows} readonly={readonly} height={height} onToggleRow={toggleRow} onChangeCell={changeCell} /> */}
      <EditableGridBody gform={gform} rows={rows} readonly={readonly} height={height} onToggleRow={toggleRow} onChangeCell={changeCell} />
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Payload Preview</DialogTitle>
        <DialogContent><pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(compileRows(rows).rcd, null, 2)}</pre></DialogContent>
      </Dialog>
      <Snackbar open={Boolean(message)} autoHideDuration={5000} onClose={() => setMessage('')}>
        <Alert severity={message.toLowerCase().includes('success') ? 'success' : 'warning'} onClose={() => setMessage('')}>{message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FEditableGrid;
